import { DEFAULT_META } from '../../../shared/constants/categories';
import { logger } from '../../../shared/utils/logger';
import { STATUS_META, STATUS_MAP, TICKET_STATUS } from '../../../shared/constants/statuses';
import moment from 'moment';

const FALLBACK_STATUS_COLOR = '#6b7280';
const DEFAULT_HEATMAP_DAY_COUNT = 7;
const HOURS_PER_DAY = 24;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const AGING_BUCKET_KEYS = Object.freeze({
  '0_3_days': '0_3',
  '4_7_days': '4_7',
  '8_14_days': '8_14',
  over_14_days: 'over_14',
});

function getStatusMeta(statusRefId, label) {
  const normalizedLabel = String(label || '').trim().toLowerCase().replace(/\s+/g, '_');
  const normalizedCode = String(statusRefId || '').trim().toUpperCase();
  const status = STATUS_MAP[normalizedCode]
    || STATUS_MAP[normalizedLabel.toUpperCase()]
    || (Object.values(TICKET_STATUS).includes(statusRefId) ? statusRefId : TICKET_STATUS.UNKNOWN);
  if (status === TICKET_STATUS.UNKNOWN) {
    logger.warn('[dashboardSummary] Unknown ticket status:', statusRefId);
  }
  const meta = STATUS_META[status];
  return {
    id: status,
    label: label || meta?.label || status,
    color: meta?.color || FALLBACK_STATUS_COLOR,
    count: 0,
  };
}

export function normalizeAging(aging) {
  if (!aging || typeof aging !== 'object' || !Array.isArray(aging.buckets)) return null;
  return {
    ...aging,
    buckets: aging.buckets.map(bucket => ({
      ...bucket,
      key: AGING_BUCKET_KEYS[bucket.key] || bucket.key,
      percentage: Number(bucket.percentage) <= 1
        ? (Number(bucket.percentage) || 0) * 100
        : Number(bucket.percentage) || 0,
    })),
  };
}

function getCategoryMeta(item, categoriesList) {
  const label = item?.label || item?.category_name || DEFAULT_META.label;
  const cate = categoriesList?.find(cat => cat.id === item?.category_ref_id);

  return {
    id: item?.category_ref_id || item?.id || item?.category_name || label,
    categoryRefId: item?.category_ref_id || item?.id || null,
    label,
    category_name: item?.category_name || label,
    icon: cate?.icon || item?.icon || DEFAULT_META.icon,
    color: cate?.color || item?.color || DEFAULT_META.color,
    count: Number(item?.count) || 0,
  };
}

function mapTrend(items) {
  return (Array.isArray(items) ? items : []).map(item => ({
    day: item.date,
    label: moment(item.date).format("MM/DD"),
    created: Number(item.created ?? item.count) || 0,
    resolved: Number(item.resolved) || 0,
  }));
}

function toDateKey(value) {
  if (value == null) return null;
  const matchedKey = typeof value === 'string'
    ? value.match(/^(\d{4}-\d{2}-\d{2})/)?.[1]
    : null;
  const date = new Date(matchedKey ? `${matchedKey}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return null;
  const key = date.toISOString().slice(0, 10);
  return !matchedKey || matchedKey === key ? key : null;
}

function shiftDateKey(key, dayOffset) {
  const date = new Date(`${key}T00:00:00Z`);
  return new Date(date.getTime() + dayOffset * MILLISECONDS_PER_DAY)
    .toISOString()
    .slice(0, 10);
}

function createHeatmapRow(key) {
  return {
    date: key,
    key,
    hours: new Array(HOURS_PER_DAY).fill(0),
    total: 0,
  };
}

function getDefaultHeatmapKeys(items, generatedAt) {
  const itemKeys = items
    .map(item => toDateKey(item?.date || item?.day || item?.key))
    .filter(Boolean);
  const generatedKey = toDateKey(generatedAt);
  const anchorKey = itemKeys.reduce(
    (latest, key) => (!latest || key > latest ? key : latest),
    generatedKey,
  ) || toDateKey(new Date());

  return Array.from(
    { length: DEFAULT_HEATMAP_DAY_COUNT },
    (_, index) => shiftDateKey(anchorKey, index - DEFAULT_HEATMAP_DAY_COUNT + 1),
  );
}

function mapHeatmap(items, { generatedAt, useDefaultRows = true } = {}) {
  const safeItems = Array.isArray(items) ? items : [];
  const rows = new Map();
  if (useDefaultRows) {
    getDefaultHeatmapKeys(safeItems, generatedAt)
      .forEach(key => rows.set(key, createHeatmapRow(key)));
  }

  safeItems.forEach(item => {
    const sourceKey = item.date || item.day || item.key;
    const key = toDateKey(sourceKey) || sourceKey;
    if (!key) return;
    if (!rows.has(key)) {
      if (useDefaultRows) return;
      rows.set(key, createHeatmapRow(key));
    }
    const row = rows.get(key);
    const hour = Number(item.hour);
    const count = Number(item.count) || 0;
    if (Number.isInteger(hour) && hour >= 0 && hour < HOURS_PER_DAY) row.hours[hour] += count;
    row.total += count;
  });
  return Array.from(rows.values());
}

function mapStatuses(items) {
  const rows = new Map(
    [...new Set(Object.values(STATUS_MAP))]
      .map(status => [status, getStatusMeta(status)]),
  );

  (Array.isArray(items) ? items : []).forEach(item => {
    const mapped = {
      ...getStatusMeta(item.status || item.status_code || item.status_ref_id || item.id, item.label),
      count: Number(item.count) || 0,
    };
    const existing = rows.get(mapped.id);
    rows.set(mapped.id, {
      ...existing,
      ...mapped,
      count: (existing?.count || 0) + mapped.count,
    });
  });

  return Array.from(rows.values());
}

function mapAgents(items) {
  return (Array.isArray(items) ? items : [])
    .map(item => ({
      id: item.user_ref_id || item.id,
      name: item.label || item.name || 'Unknown',
      email: item.email || '',
      initials: item.initials || '',
      hue: item.hue || '',
      team: item.team || '',
      load: Number(item.count) || 0,
    }))
    .filter(item => item.id);
}

export function mapDashboardSummaryToStats(summary, categoriesList) {
  if (!summary || typeof summary !== 'object') return null;

  const statusData = mapStatuses(summary.by_status);
  
  const categoryData = (Array.isArray(summary.by_category) ? summary.by_category : [])
    .map(item => getCategoryMeta(item, categoriesList));

  const total = Number(summary.total) || 0;
  const open = statusData
    .filter(item => [TICKET_STATUS.NEW, TICKET_STATUS.OPEN, TICKET_STATUS.IN_PROGRESS].includes(item.id))
    .reduce((count, item) => count + item.count, 0);
  const overdue = Number(summary.overdue ?? summary.aging?.overdue) || 0;
  const notAssigned = Number(summary.not_assigned) || 0;
  const overdueByPriority = summary.overdue_by_priority || summary.by_priority || {};
  const oldestWaiting = summary.oldest_waiting || summary.oldest_waiting_duration || null;

  return {
    total,
    open,
    overdue,
    notAssigned,
    overdueByPriority,
    oldestWaiting,
    // slaCompliance: Number.isFinite(Number(summary.sla_compliance))
    //   ? Number(summary.sla_compliance)
    //   : (overdue > 0 && total > 0 ? Math.round((1 - overdue / total) * 100) : 100),
    byStatus: statusData.reduce((result, item) => {
      result[item.id] = item.count;
      return result;
    }, {}),
    statusData,
    trend: mapTrend(summary.trend),
    trendBucketUnit: summary.granularity || 'day',
    categoryData,
    agents: mapAgents(summary.by_pic),
    heatmap: mapHeatmap(summary.volume_by_hour, {
      generatedAt: summary.generated_at,
      useDefaultRows: !summary.range?.start_date && !summary.range?.end_date,
    }),
    aging: normalizeAging(summary.aging),
  };
}
